import { type ReactNode } from 'react';

import { type ToolbarAction, type ToolbarTableProps } from '@/components/core/ToolbarTable';
import { DEFAULT_TOOLBAR_COUNT_LIMIT } from '@/components/core/ToolbarTable/toolbarLayout';
import { useTranslation } from '@/hooks/useTranslation';
import { useBrowseDeleteAction } from '@/pages/browse/delete-items';
import { useDownloadAsAction } from '@/pages/browse/download/useDownloadAsAction';
import { type ItemsListMeta } from '@/pages/browse/types';

import { type BrowseToolbarSelection, itemRestrictions, selectedPrimaryKeys } from './toolbarSelection';
import { useAddTaskAction } from './useAddTaskAction';
import { useAssetExportAction } from './useAssetExportAction';
import { useMassUpdateAction } from './useMassUpdateAction';
import { usePublishAction } from './usePublishAction';
import { useValidateAction } from './useValidateAction';

type BrowseToolbarActionsInput = BrowseToolbarSelection & {
    itemsMeta?: ItemsListMeta | null;
    /** Exact total from the refresh icon. Replaces the count limit. */
    exactCount?: number;
};

type BrowseToolbarCounter = Pick<
    ToolbarTableProps,
    'selectedCount' | 'totalCount' | 'allSelected' | 'overCountLimit' | 'countLimit'
>;

type BrowseToolbarActions = BrowseToolbarCounter & {
    /** Download as, Publish, Mass update, Asset export, Add task, Validate, Delete. */
    actions: ToolbarAction[];
    /** Render beside the toolbar that receives `actions`. */
    dialogs: ReactNode;
};

const resolveCounter = (input: BrowseToolbarActionsInput): BrowseToolbarCounter => {
    const numberFound = input.itemsMeta?.numberFound ?? 0;
    const maxNumberFound = input.itemsMeta?.maxNumberFound || DEFAULT_TOOLBAR_COUNT_LIMIT;
    const overCountLimit = input.exactCount == null && numberFound > maxNumberFound;
    const totalCount = input.exactCount ?? (overCountLimit ? maxNumberFound : numberFound);
    return {
        selectedCount: input.allItemsSelected ? totalCount : selectedPrimaryKeys(input).length,
        totalCount,
        allSelected: input.allItemsSelected,
        overCountLimit,
        countLimit: maxNumberFound,
    };
};

/**
 * The browse toolbar actions in their fixed order, with the dialogs they
 * open and the counter props for {@link ToolbarTable}. A missing permission
 * drops the action. The toolbar itself locks everything while nothing is
 * selected, and Validate is disabled in global select-all.
 *
 * Not mounted on BrowsePage yet. Spread the counter into `ToolbarTable`,
 * pass `actions`, and render `dialogs` next to it.
 */
const useBrowseToolbarActions = (input: BrowseToolbarActionsInput): BrowseToolbarActions => {
    const { t } = useTranslation();
    const counter = resolveCounter(input);

    const download = useDownloadAsAction({
        allItemsSelected: input.allItemsSelected,
        selectedItems: input.selectedItems,
        itemsLoading: input.itemsMeta?.isFetching,
        primaryKeys: selectedPrimaryKeys(input),
        itemsQuery: itemRestrictions(input),
    });
    const publish = usePublishAction(input);
    const massUpdate = useMassUpdateAction(input, {
        numberFound: input.itemsMeta?.numberFound,
        maxNumberFound: input.itemsMeta?.maxNumberFound,
    });
    const assetExport = useAssetExportAction(input);
    const addTask = useAddTaskAction(input);
    const validate = useValidateAction(input);
    const remove = useBrowseDeleteAction({
        ...input,
        showWithoutSelection: true,
    });

    const actions = [
        download.visible ? { ...download.action, label: t('TOOLBAR.DOWNLOAD_AS') } : null,
        publish,
        massUpdate,
        assetExport,
        addTask.action,
        validate,
        remove.action ? { ...remove.action, label: t('TOOLBAR.DELETE') } : null,
    ].filter((action): action is ToolbarAction => action != null);

    return {
        ...counter,
        actions,
        dialogs: (
            <>
                {download.dialog}
                {addTask.dialog}
                {remove.dialog}
            </>
        ),
    };
};

export type { BrowseToolbarActions, BrowseToolbarActionsInput, BrowseToolbarCounter };
export { resolveCounter, useBrowseToolbarActions };
